import { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { FaDollarSign, FaCarSide, FaExchangeAlt, FaOilCan, FaSearchLocation, FaExclamationTriangle, FaPlay, FaStop, FaReceipt } from "react-icons/fa";
import api from "../../services/api";
import { toast } from "react-toastify";

interface TimelineEvent {
    data: string;
    tipo: string;
    titulo: string;
    descricao: string;
    status: string;
    cor: string;
    valor?: number;
}

interface ResumoLocacao {
    idLocacao: string;
    locatario: string;
    veiculo: string;
    statusLocacao: string;
    dataInicio: string;
    dataTermino?: string;
    valorAluguel: number;
    eventos: TimelineEvent[];
}

interface Props {
    show: boolean;
    onHide: () => void;
    idLocacao: string | null;
}

const corMap: Record<string, string> = {
    success: "#16a34a",
    danger: "#dc2626",
    warning: "#d97706",
    info: "#2563eb",
    secondary: "#64748b",
};

const corBgMap: Record<string, string> = {
    success: "#f0fdf4",
    danger: "#fef2f2",
    warning: "#fffbeb",
    info: "#eff6ff",
    secondary: "#f8fafc",
};

const iconeMap: Record<string, React.ReactNode> = {
    Pagamento: <FaDollarSign size={14} />,
    Multa: <FaExclamationTriangle size={14} />,
    Vistoria: <FaSearchLocation size={14} />,
    TrocaDeOleo: <FaOilCan size={14} />,
    SubstituicaoVeiculo: <FaExchangeAlt size={14} />,
    Lancamento: <FaReceipt size={14} />,
    Locacao: <FaPlay size={14} />,
};

const statusCorLocacao: Record<string, { bg: string; text: string }> = {
    Ativa: { bg: "#dcfce7", text: "#166534" },
    Inativa: { bg: "#fef3c7", text: "#92400e" },
    Encerrada: { bg: "#f1f5f9", text: "#475569" },
};

function formatarData(dataStr: string) {
    const d = new Date(dataStr);
    return d.toLocaleDateString("pt-BR");
}

function formatarMesAno(dataStr: string) {
    const d = new Date(dataStr);
    const meses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
    return `${meses[d.getMonth()]} ${d.getFullYear()}`;
}

function agruparPorMes(eventos: TimelineEvent[]) {
    const grupos: { mesAno: string; eventos: TimelineEvent[] }[] = [];
    let mesAtual = "";

    for (const evento of eventos) {
        const mesAno = formatarMesAno(evento.data);
        if (mesAno !== mesAtual) {
            mesAtual = mesAno;
            grupos.push({ mesAno, eventos: [] });
        }
        grupos[grupos.length - 1].eventos.push(evento);
    }

    return grupos;
}

export default function ModalResumoLocacao({ show, onHide, idLocacao }: Props) {
    const [resumo, setResumo] = useState<ResumoLocacao | null>(null);
    const [carregando, setCarregando] = useState(false);
    const [filtroTipo, setFiltroTipo] = useState<string>("Todos");

    useEffect(() => {
        if (show && idLocacao) {
            setCarregando(true);
            setFiltroTipo("Todos");
            api.get(`/Locacao/Resumo/${idLocacao}`)
                .then((res) => setResumo(res.data))
                .catch(() => toast.error("Erro ao carregar resumo da locação"))
                .finally(() => setCarregando(false));
        } else {
            setResumo(null);
        }
    }, [show, idLocacao]);

    const tiposDisponiveis = resumo
        ? ["Todos", ...Array.from(new Set(resumo.eventos.map((e) => e.tipo)))]
        : [];

    const labelTipo: Record<string, string> = {
        Todos: "Todos",
        Pagamento: "Pagamentos",
        Multa: "Multas",
        Vistoria: "Vistorias",
        TrocaDeOleo: "Trocas de Óleo",
        SubstituicaoVeiculo: "Substituições",
        Lancamento: "Lançamentos",
        Locacao: "Locação",
    };

    const eventosFiltrados = resumo
        ? filtroTipo === "Todos"
            ? resumo.eventos
            : resumo.eventos.filter((e) => e.tipo === filtroTipo)
        : [];

    const grupos = agruparPorMes(eventosFiltrados);
    const statusStyle = resumo ? statusCorLocacao[resumo.statusLocacao] || statusCorLocacao.Encerrada : statusCorLocacao.Encerrada;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "20px 24px" }}>
                <div style={{ width: "100%" }}>
                    <Modal.Title style={{ fontSize: 18, fontWeight: 700, color: "#1A202C", marginBottom: 8 }}>
                        Resumo da Locação
                    </Modal.Title>
                    {resumo && (
                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ fontSize: 13, color: "#4A5568" }}>
                                <strong>Locatário:</strong> {resumo.locatario}
                            </span>
                            <span style={{ fontSize: 13, color: "#4A5568" }}>
                                <strong>Veículo:</strong> {resumo.veiculo}
                            </span>
                            <span style={{ fontSize: 13, color: "#4A5568" }}>
                                <strong>Valor:</strong> R$ {resumo.valorAluguel.toFixed(2).replace(".", ",")}
                            </span>
                            <span
                                style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    padding: "2px 10px",
                                    borderRadius: 12,
                                    background: statusStyle.bg,
                                    color: statusStyle.text,
                                }}
                            >
                                {resumo.statusLocacao}
                            </span>
                        </div>
                    )}
                </div>
            </Modal.Header>

            <Modal.Body style={{ padding: 0, maxHeight: "65vh", overflowY: "auto" }}>
                {carregando ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 60 }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : !resumo ? (
                    <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
                        Nenhum dado encontrado.
                    </div>
                ) : (
                    <>
                        {/* Filtros */}
                        <div style={{ padding: "16px 24px 8px", display: "flex", gap: 8, flexWrap: "wrap", borderBottom: "1px solid #e2e8f0" }}>
                            {tiposDisponiveis.map((tipo) => (
                                <button
                                    key={tipo}
                                    onClick={() => setFiltroTipo(tipo)}
                                    style={{
                                        padding: "4px 14px",
                                        borderRadius: 20,
                                        border: filtroTipo === tipo ? "1px solid #1F467E" : "1px solid #e2e8f0",
                                        background: filtroTipo === tipo ? "#1F467E" : "#fff",
                                        color: filtroTipo === tipo ? "#fff" : "#4A5568",
                                        fontSize: 12,
                                        fontWeight: 500,
                                        cursor: "pointer",
                                        marginBottom: 8,
                                    }}
                                >
                                    {labelTipo[tipo] || tipo}
                                </button>
                            ))}
                        </div>

                        {/* Timeline */}
                        <div style={{ padding: "20px 24px" }}>
                            {grupos.length === 0 && (
                                <div style={{ textAlign: "center", color: "#94a3b8", padding: 20 }}>
                                    Nenhum evento encontrado para este filtro.
                                </div>
                            )}

                            {grupos.map((grupo, gi) => (
                                <div key={gi} style={{ marginBottom: 24 }}>
                                    {/* Mês/Ano */}
                                    <div
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: "#1F467E",
                                            textTransform: "uppercase",
                                            letterSpacing: 1,
                                            marginBottom: 12,
                                            paddingBottom: 6,
                                            borderBottom: "1px solid #e2e8f0",
                                        }}
                                    >
                                        {grupo.mesAno}
                                    </div>

                                    {grupo.eventos.map((evento, ei) => {
                                        const cor = corMap[evento.cor] || corMap.secondary;
                                        const corBg = corBgMap[evento.cor] || corBgMap.secondary;
                                        const icone = evento.tipo === "Locacao" && evento.titulo.includes("Encerrada")
                                            ? <FaStop size={14} />
                                            : iconeMap[evento.tipo] || <FaCarSide size={14} />;
                                        const isLast = ei === grupo.eventos.length - 1;

                                        return (
                                            <div key={ei} style={{ display: "flex", gap: 16, position: "relative" }}>
                                                {/* Linha vertical + bolinha */}
                                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 32, flexShrink: 0 }}>
                                                    <div
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: "50%",
                                                            background: corBg,
                                                            border: `2px solid ${cor}`,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color: cor,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {icone}
                                                    </div>
                                                    {!isLast && (
                                                        <div style={{ width: 2, flexGrow: 1, background: "#e2e8f0", minHeight: 16 }} />
                                                    )}
                                                </div>

                                                {/* Conteúdo */}
                                                <div style={{ flex: 1, paddingBottom: isLast ? 0 : 16 }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                        <span style={{ fontSize: 14, fontWeight: 600, color: "#1A202C" }}>
                                                            {evento.titulo}
                                                        </span>
                                                        <span
                                                            style={{
                                                                fontSize: 10,
                                                                fontWeight: 600,
                                                                padding: "2px 8px",
                                                                borderRadius: 10,
                                                                background: corBg,
                                                                color: cor,
                                                                border: `1px solid ${cor}20`,
                                                            }}
                                                        >
                                                            {evento.status}
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                                                        {evento.descricao}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                                                        {formatarData(evento.data)}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0", padding: "12px 24px" }}>
                {resumo && (
                    <span style={{ fontSize: 12, color: "#94a3b8", marginRight: "auto" }}>
                        {eventosFiltrados.length} evento{eventosFiltrados.length !== 1 ? "s" : ""}
                    </span>
                )}
                <button
                    onClick={onHide}
                    style={{
                        padding: "8px 24px",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        background: "#fff",
                        color: "#4A5568",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                    }}
                >
                    Fechar
                </button>
            </Modal.Footer>
        </Modal>
    );
}
